const buildingSilhouetteCache = new Map<string, HTMLCanvasElement>();

export type BuildingDrawSource = HTMLImageElement | HTMLCanvasElement;

function buildingSourceSize(img: BuildingDrawSource): { width: number; height: number } {
  if (img instanceof HTMLImageElement) {
    return { width: img.naturalWidth, height: img.naturalHeight };
  }
  return { width: img.width, height: img.height };
}

export function buildingDrawRect({
  sourceWidth,
  sourceHeight,
  tileX,
  tileY,
  tileSize,
}: {
  sourceWidth: number;
  sourceHeight: number;
  tileX: number;
  tileY: number;
  tileSize: number;
}): { x: number; y: number; width: number; height: number } {
  const width = sourceWidth > 0 ? sourceWidth : tileSize;
  const height = sourceHeight > 0 ? sourceHeight : tileSize;
  return {
    x: tileX * tileSize + (tileSize - width) / 2,
    y: tileY * tileSize + (tileSize - height) / 2,
    width,
    height,
  };
}

function getBuildingImageSilhouette(img: HTMLImageElement): HTMLCanvasElement | null {
  if (!img.complete || img.naturalWidth <= 0 || img.naturalHeight <= 0) return null;
  const key = img.src;
  const cached = buildingSilhouetteCache.get(key);
  if (cached) return cached;
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0);
  ctx.globalCompositeOperation = "source-in";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  buildingSilhouetteCache.set(key, canvas);
  return canvas;
}

export function drawBuildingImage({
  ctx,
  img,
  tileX,
  tileY,
  tileSize,
  flipX = false,
}: {
  ctx: CanvasRenderingContext2D;
  img: BuildingDrawSource;
  tileX: number;
  tileY: number;
  tileSize: number;
  flipX?: boolean;
}): void {
  const size = buildingSourceSize(img);
  const rect = buildingDrawRect({
    sourceWidth: size.width,
    sourceHeight: size.height,
    tileX,
    tileY,
    tileSize,
  });
  ctx.imageSmoothingEnabled = false;
  if (flipX) {
    ctx.save();
    ctx.translate(rect.x + rect.width, rect.y);
    ctx.scale(-1, 1);
    ctx.drawImage(img, 0, 0, rect.width, rect.height);
    ctx.restore();
    return;
  }
  ctx.drawImage(img, rect.x, rect.y, rect.width, rect.height);
}

export function drawBuildingHitFlash({
  ctx,
  img,
  tileX,
  tileY,
  tileSize,
  hitFlash,
}: {
  ctx: CanvasRenderingContext2D;
  img: HTMLImageElement;
  tileX: number;
  tileY: number;
  tileSize: number;
  hitFlash: number;
}): void {
  if (hitFlash <= 0) return;
  const whiteMask = getBuildingImageSilhouette(img);
  if (!whiteMask) return;
  ctx.save();
  ctx.globalAlpha *= Math.min(1, hitFlash);
  drawBuildingImage({ ctx, img: whiteMask, tileX, tileY, tileSize });
  ctx.restore();
}
